// @ts-check
// Estado da página de painéis: lista, painel aberto, aba aberta e o rascunho do modo de edição (doc 16, 8.1, 8.2 e
// 8.4). O rascunho (abas e widgets) vive em memória até "Salvar" (PUT único de abas e grade) ou "Cancelar"
// (descarta sem confirmação). Cada aba tem a própria grade: posições e colisões contam só dentro da aba.
import { signal, computed } from '@preact/signals';
import { getDashboard, listDashboards, saveDashboardWidgets } from '../../data/dashboards.js';
import { DEFAULT_WIDGET_SIZES, getBottomRow, placeItem } from '../../lib/dashboard-grid.js';
import {
  addTab, duplicateTab, moveTab, moveWidgetToTab, nextTabTitle, removeTab, resolveActiveTabKey, toSavedTabs, widgetsInTab,
} from '../../lib/dashboard-tabs.js';
import { generateId } from '../../lib/utils.js';
import { applyLeadQualityMeasures } from '../../lib/lead-quality-draft.js';
import { clearWidgetDataCache } from './widget-data.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/dashboards.js').Dashboard} Dashboard
 * @typedef {import('../../data/dashboards.js').DashboardSummary} DashboardSummary
 * @typedef {import('../../data/dashboards.js').DashboardWidget} DashboardWidget
 * @typedef {import('../../data/dashboards.js').WidgetType} WidgetType
 * @typedef {import('../../lib/dashboard-tabs.js').DraftTab} DraftTab
 * @typedef {DashboardWidget & { key: string }} DraftWidget
 */

/** Aba usada quando o painel veio sem abas (servidor anterior às abas): some no salvamento. */
const FALLBACK_TAB_KEY = '__default';

/** @type {import('@preact/signals').Signal<DashboardSummary[] | null>} */
export const dashboardsState = signal(null);
/** @type {import('@preact/signals').Signal<Dashboard | null>} */
export const currentDashboardState = signal(null);
export const isEditingState = signal(false);
/** @type {import('@preact/signals').Signal<DraftWidget[]>} */
export const draftWidgetsState = signal([]);
/** @type {import('@preact/signals').Signal<DraftTab[]>} */
export const draftTabsState = signal([]);
/** Aba pedida (URL ou clique); a aberta de fato é `activeTabKeyState`, que cai na primeira quando a pedida não existe. */
/** @type {import('@preact/signals').Signal<string | null>} */
export const requestedTabKeyState = signal(null);
/** @type {import('@preact/signals').Signal<string | null>} */
export const selectedWidgetKeyState = signal(null);
/** Filtros atuais do painel, lidos também pela pré-visualização do painel lateral. */
/** @type {import('@preact/signals').Signal<import('../../data/dashboards.js').DashboardRequestFilters>} */
export const requestFiltersState = signal({});

/**
 * @param {Dashboard | null} dashboard
 * @returns {DraftTab[]}
 */
function toDraftTabs(dashboard) {
  const tabs = dashboard?.tabs ?? [];
  if (tabs.length === 0) return [{ key: FALLBACK_TAB_KEY, title: t('dashboardParity.tabs.defaultTitle'), icon: null }];
  return tabs.map((tab) => ({ key: tab.id, id: tab.id, title: tab.title, icon: tab.icon ?? null }));
}

/** Abas visíveis: as do rascunho em edição, as do painel fora dela. */
export const tabsState = computed(() => (isEditingState.value ? draftTabsState.value : toDraftTabs(currentDashboardState.value)));
export const activeTabKeyState = computed(() => resolveActiveTabKey(tabsState.value, requestedTabKeyState.value));

export const isDraftDirtyState = computed(() => {
  const dashboard = currentDashboardState.value;
  if (!isEditingState.value || !dashboard) return false;
  const widgetsChanged = JSON.stringify(draftWidgetsState.value.map(stripKey)) !== JSON.stringify(dashboard.widgets);
  return widgetsChanged || JSON.stringify(toSavedTabs(draftTabsState.value)) !== JSON.stringify(toSavedTabs(toDraftTabs(dashboard)));
});

/**
 * @param {DraftWidget} widget
 * @returns {DashboardWidget}
 */
function stripKey({ key, ...widget }) {
  return widget;
}

/**
 * @param {DashboardWidget} widget
 * @returns {DraftWidget}
 */
function toDraftWidget(widget) {
  return { ...structuredClone(widget), key: widget.id ?? `draft-${generateId()}` };
}

const firstTabKey = () => tabsState.value[0]?.key ?? null;

/** Widgets do rascunho que estão na aba aberta. */
export const activeTabDraftWidgetsState = computed(() => widgetsInTab(draftWidgetsState.value, activeTabKeyState.value, firstTabKey()));

export async function refreshDashboards() {
  dashboardsState.value = await listDashboards();
  return dashboardsState.value;
}

/**
 * @param {string} id
 */
export async function openDashboard(id) {
  const dashboard = await getDashboard(id);
  currentDashboardState.value = dashboard;
  return dashboard;
}

/**
 * @param {string | null} tabKey
 */
export function selectTab(tabKey) {
  requestedTabKeyState.value = tabKey;
  selectedWidgetKeyState.value = null;
}

/**
 * @param {Dashboard} dashboard
 */
export function startEditing(dashboard) {
  draftTabsState.value = toDraftTabs(dashboard);
  draftWidgetsState.value = dashboard.widgets.map(toDraftWidget);
  selectedWidgetKeyState.value = null;
  isEditingState.value = true;
}

export function stopEditing() {
  isEditingState.value = false;
  draftWidgetsState.value = [];
  draftTabsState.value = [];
  selectedWidgetKeyState.value = null;
}

/**
 * @param {string} key
 * @param {Partial<DashboardWidget>} changes
 */
export function updateDraftWidget(key, changes) {
  draftWidgetsState.value = draftWidgetsState.value.map((widget) => (widget.key === key ? { ...widget, ...changes } : widget));
}

/**
 * Campos medidos da qualidade do lead no cartão e, com "aplicar a todos", nos outros cartões de qualidade do rascunho
 * (salvos juntos no mesmo PUT da grade).
 * @param {string} key @param {string[] | undefined} measures @param {boolean} applyToAll
 */
export function setDraftLeadQualityMeasures(key, measures, applyToAll) {
  draftWidgetsState.value = applyLeadQualityMeasures(draftWidgetsState.value, key, measures, { applyToAll });
}

/**
 * @param {string} key
 * @param {Record<string, any>} changes
 */
export function updateDraftConfiguration(key, changes) {
  draftWidgetsState.value = draftWidgetsState.value.map((widget) =>
    widget.key === key ? { ...widget, configuration: { ...widget.configuration, ...changes } } : widget,
  );
}

/**
 * Nova arrumação dos widgets de uma aba (a grade só conhece a aba aberta): troca os itens pela chave.
 * @param {DraftWidget[]} widgets
 */
export function setDraftLayout(widgets) {
  const byKey = new Map(widgets.map((widget) => [widget.key, widget]));
  draftWidgetsState.value = draftWidgetsState.value.map((widget) => byKey.get(widget.key) ?? widget);
}

/**
 * Coloca um widget num retângulo da aba dele, empurrando só os vizinhos da mesma aba.
 * @param {string} key
 * @param {import('../../lib/dashboard-grid.js').GridRectangle} rectangle
 */
function placeInTab(key, rectangle) {
  const widget = draftWidgetsState.value.find((candidate) => candidate.key === key);
  if (!widget) return;
  const tabWidgets = widgetsInTab(draftWidgetsState.value, widget.tabId ?? firstTabKey(), firstTabKey());
  setDraftLayout(placeItem(tabWidgets, key, rectangle));
}

/**
 * Widget novo no fim da aba aberta, com o tamanho padrão do tipo.
 * @param {WidgetType} type
 * @param {{ title?: string, configuration?: Record<string, any>, size?: { columnSpan: number, rowSpan: number } }} [options]
 */
export function addDraftWidget(type, { title, configuration = {}, size } = {}) {
  const resolvedSize = size ?? DEFAULT_WIDGET_SIZES[type];
  /** @type {DraftWidget} */
  const widget = {
    key: `draft-${generateId()}`,
    type,
    title: title ?? t(`dashboards.widgetTypes.${type}`),
    configuration,
    gridRow: getBottomRow(activeTabDraftWidgetsState.value),
    gridColumn: 0,
    rowSpan: resolvedSize.rowSpan,
    columnSpan: resolvedSize.columnSpan,
    tabId: activeTabKeyState.value,
  };
  draftWidgetsState.value = [...draftWidgetsState.value, widget];
  selectedWidgetKeyState.value = widget.key;
  return widget;
}

/**
 * Troca o tipo mantendo a área ocupada (empurra os vizinhos se o novo tipo for maior).
 * @param {string} key
 * @param {WidgetType} type
 * @param {Record<string, any>} configuration
 */
export function changeDraftWidgetType(key, type, configuration) {
  const widget = draftWidgetsState.value.find((candidate) => candidate.key === key);
  if (!widget) return;
  const size = DEFAULT_WIDGET_SIZES[type];
  draftWidgetsState.value = draftWidgetsState.value.map((candidate) => (candidate.key === key ? { ...candidate, type, configuration } : candidate));
  placeInTab(key, {
    gridRow: widget.gridRow,
    gridColumn: Math.min(widget.gridColumn, 12 - size.columnSpan),
    columnSpan: size.columnSpan,
    rowSpan: size.rowSpan,
  });
}

/**
 * Novo tamanho ou posição de um widget, empurrando os vizinhos que colidirem.
 * @param {string} key
 * @param {import('../../lib/dashboard-grid.js').GridRectangle} rectangle
 */
export function setDraftWidgetRectangle(key, rectangle) {
  placeInTab(key, rectangle);
}

/**
 * Cópia logo abaixo de tudo na mesma aba, com o mesmo tamanho e coluna (doc 16, 8.3).
 * @param {string} key
 */
export function duplicateDraftWidget(key) {
  const widget = draftWidgetsState.value.find((candidate) => candidate.key === key);
  if (!widget) return null;
  const tabKey = widget.tabId ?? firstTabKey();
  /** @type {DraftWidget} */
  const copy = {
    ...structuredClone(stripKey(widget)),
    id: undefined,
    key: `draft-${generateId()}`,
    title: t('dashboards.widget.copyTitle', { title: widget.title }),
    gridRow: getBottomRow(widgetsInTab(draftWidgetsState.value, tabKey, firstTabKey())),
    tabId: tabKey,
  };
  delete copy.id;
  draftWidgetsState.value = [...draftWidgetsState.value, copy];
  selectedWidgetKeyState.value = copy.key;
  return copy;
}

/**
 * @param {string} key
 */
export function removeDraftWidget(key) {
  draftWidgetsState.value = draftWidgetsState.value.filter((widget) => widget.key !== key);
  if (selectedWidgetKeyState.value === key) selectedWidgetKeyState.value = null;
}

/**
 * Leva o widget para outra aba (abaixo do último dela) e abre essa aba.
 * @param {string} widgetKey
 * @param {string} tabKey
 */
export function moveDraftWidgetToTab(widgetKey, tabKey) {
  draftWidgetsState.value = moveWidgetToTab(draftWidgetsState.value, widgetKey, tabKey, firstTabKey());
  requestedTabKeyState.value = tabKey;
}

/** Aba nova no fim, já aberta; devolve a chave. */
export function addDraftTab() {
  const key = `draft-tab-${generateId()}`;
  const title = nextTabTitle(draftTabsState.value, (number) => t('dashboardParity.tabs.numberedTitle', { number }));
  const next = addTab(draftTabsState.value, { key, title });
  if (next === draftTabsState.value) return null;
  draftTabsState.value = next;
  selectTab(key);
  return key;
}

/**
 * @param {string} tabKey
 * @param {{ title?: string, icon?: string | null }} changes
 */
export function updateDraftTab(tabKey, changes) {
  draftTabsState.value = draftTabsState.value.map((tab) => (tab.key === tabKey ? { ...tab, ...changes } : tab));
}

/**
 * @param {string} tabKey
 * @param {-1 | 1} direction
 */
export function moveDraftTab(tabKey, direction) {
  draftTabsState.value = moveTab(draftTabsState.value, tabKey, direction);
}

/**
 * @param {string} tabKey
 */
export function duplicateDraftTab(tabKey) {
  const source = draftTabsState.value.find((tab) => tab.key === tabKey);
  if (!source) return null;
  const newTabKey = `draft-tab-${generateId()}`;
  const result = duplicateTab(draftTabsState.value, draftWidgetsState.value, tabKey, {
    newTabKey,
    title: t('dashboardParity.tabs.copyTitle', { title: source.title }),
    newWidgetKey: () => `draft-${generateId()}`,
  });
  if (result.tabs === draftTabsState.value) return null;
  draftTabsState.value = result.tabs;
  draftWidgetsState.value = result.widgets;
  selectTab(newTabKey);
  return newTabKey;
}

/**
 * Exclui a aba e os widgets dela (a última aba não sai).
 * @param {string} tabKey
 */
export function removeDraftTab(tabKey) {
  const result = removeTab(draftTabsState.value, draftWidgetsState.value, tabKey);
  if (result.tabs === draftTabsState.value) return false;
  draftTabsState.value = result.tabs;
  draftWidgetsState.value = result.widgets;
  selectTab(result.activeTabKey);
  return true;
}

/**
 * Salva abas e grade e sai da edição; em erro, continua editando (o chamador mostra o aviso).
 */
export async function saveDraft() {
  const dashboard = currentDashboardState.value;
  if (!dashboard) return null;
  const tabs = draftTabsState.value;
  const hasRealTabs = !(tabs.length === 1 && tabs[0].key === FALLBACK_TAB_KEY);
  const widgets = draftWidgetsState.value.map((widget) => {
    const { key, ...rest } = widget;
    const tabId = hasRealTabs ? rest.tabId ?? tabs[0]?.key : undefined;
    return key.startsWith('draft-') ? { ...rest, id: undefined, tabId } : { ...rest, tabId };
  });
  const activeTab = tabs.find((tab) => tab.key === activeTabKeyState.value);
  const activeIndex = activeTab ? tabs.indexOf(activeTab) : 0;
  const saved = await saveDashboardWidgets(dashboard.id, widgets, hasRealTabs ? toSavedTabs(tabs) : undefined);
  clearWidgetDataCache();
  // Sem corpo útil na resposta, relê o painel para ter os ids definitivos.
  const next = saved?.widgets ? { ...dashboard, ...saved, tabs: saved.tabs ?? dashboard.tabs } : await getDashboard(dashboard.id);
  currentDashboardState.value = next;
  stopEditing();
  // a aba nova ganhou id no servidor: continua aberta pela posição
  requestedTabKeyState.value = next.tabs?.[activeIndex]?.id ?? next.tabs?.[0]?.id ?? null;
  return currentDashboardState.value;
}
